// @ts-nocheck
/* eslint-disable */
import React, { useState } from 'react';
import axios from 'axios';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';

/*
 * Applicable-fares panel, shared by the fare product, promotion and discount
 * band assignment screens.
 *
 * One button, one table. Base fares and zone prices are two tiers of the SAME
 * price — a tap outside every zone polygon falls back to the axis base fare —
 * so showing them in separate panels invited exactly the mistake this guards
 * against. Each axis is one block: its base fare, then the zones sitting on it.
 *
 * A fixed-amount discount larger than the fare does NOT error at tap time; the
 * pricing functions clamp with LEAST(price, discount_amount), so the trip
 * silently becomes free. The floor is therefore the number that matters, and it
 * is computed server-side by utils/fareFloor.js so the panel and the save can
 * never disagree.
 */

const API_BASE = `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}/api/v1/web_portal/fare_media_policy_capping`;

const ENDPOINTS = {
  band: `${API_BASE}/discount_band/check_applicable_fares`,
  assignment: `${API_BASE}/discount_band_assignment/check_applicable_fares`,
};

// Neutral surface + a single accent, used only where something is wrong.
// Colour carries one meaning here; anything else is grey.
const INK = '#1f2937';
const MUTED = '#6b7280';
const LINE = '#e5e7eb';
const SURFACE = '#fafafa';
const ACCENT = '#b42318';
const ACCENT_SOFT = '#fdf3f2';

const authConfig = () => {
  let authData = null;
  const localAuthData = localStorage.getItem('authData');
  if (localAuthData) {
    authData = JSON.parse(localAuthData).userToken;
  }
  return {
    headers: {
      'access-token': authData?.['access-token'] || authData?.access_token,
      'uid': authData?.uid,
      'client': authData?.client,
      'Authorization': 'Bearer ' + localStorage.getItem('dashboard_token'),
    },
  };
};

export const fetchApplicableFares = async (scope, body) => {
  const res = await axios.post(ENDPOINTS[scope], body, authConfig());
  return res.data.data;
};

const money = (v) => (v === null || v === undefined ? '—' : `${v}`);
const t = (intl, id, fallback) => intl?.formatMessage({ id }) || fallback;

// Built client-side, never taken from the API: a label the admin reads has to
// follow their language, and the backend has no business knowing it.
const sourceLabel = (source, name, intl) => {
  if (!source) return '';
  if (source === 'ZONE') {
    const n = name || '';
    return n ? `${t(intl, 'Zone', 'Zone')} "${n}"` : t(intl, 'Zone', 'Zone');
  }
  return t(intl, 'BaseFareLabel', 'Base fare');
};

/**
 * Blocking check for AddEntity/EditEntity `beforeSubmit`.
 * Re-fetches rather than trusting panel state, because the admin is not
 * required to open the panel before saving.
 *
 * @returns {Promise<boolean>} true if the save may proceed
 */
export const passesFareGuard = async ({ scope, body, discountAmount, intl }) => {
  // Percentage discounts are bounded by 100% and can never exceed the fare.
  if (!Number(discountAmount)) return true;

  let data;
  try {
    data = await fetchApplicableFares(scope, { ...body, discount_amount: discountAmount });
  } catch (e) {
    // Never let an unreachable guard block a save the backend would accept —
    // the same rule is enforced server-side on the write itself.
    toast.warn(t(intl, 'FailedToFetchApplicableFares', 'Could not verify applicable fares.'));
    return true;
  }

  // Each level blocks on what its own save enforces, and nothing else — a
  // warning that still lets the record through only teaches admins to ignore it.
  // Band: organisation-wide ceiling, since a band carries no route scope.
  // Assignment: the true floor for the selected routes.
  if (scope === 'band' && data?.exceeds_max) {
    toast.error(`${t(intl, 'DiscountExceedsMaxFare', 'Discount exceeds the highest active fare')} (${money(data.combined_max_fare)}).`);
    return false;
  }

  if (scope === 'assignment' && data?.would_be_rejected) {
    const where = sourceLabel(data.min_fare_source, data.min_fare_source_name, intl);
    toast.error(
      `${t(intl, 'DiscountExceedsFloor', 'Discount exceeds the lowest chargeable fare on the selected routes')} ` +
      `(${money(data.combined_min_fare)}${where ? ' — ' + where : ''}).`
    );
    return false;
  }

  return true;
};

const Stat = ({ label, value, note, flagged }) => (
  <div style={{ flex: '1 1 0', padding: '12px 16px', borderRight: `1px solid ${LINE}`, minWidth: 0 }}>
    <div style={{ fontSize: '0.72rem', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontSize: '1.25rem', fontWeight: 600, color: flagged ? ACCENT : INK, lineHeight: 1.2 }}>
      {value}
    </div>
    {note && <div style={{ fontSize: '0.75rem', color: MUTED, marginTop: 3 }}>{note}</div>}
  </div>
);

const cell = { padding: '7px 12px', borderBottom: `1px solid ${LINE}`, fontSize: '0.84rem', color: INK };

const PriceRow = ({ label, sublabel, cash, card, lowest, affected, indented, intl }) => (
  // Every row under the discount is marked, not just the single cheapest —
  // that is what makes the table actionable rather than merely informative.
  <tr style={{ background: affected ? ACCENT_SOFT : 'transparent' }}>
    <td style={{ ...cell, paddingLeft: indented ? 32 : 12, borderLeft: `3px solid ${affected ? ACCENT : 'transparent'}` }}>
      {label}
      {sublabel && <span style={{ color: MUTED, marginLeft: 6, fontSize: '0.78rem' }}>{sublabel}</span>}
    </td>
    <td style={{ ...cell, color: MUTED }}>{money(cash)}</td>
    <td style={{ ...cell, color: MUTED }}>{money(card)}</td>
    <td style={{ ...cell, fontWeight: 600, color: affected ? ACCENT : INK }}>
      {money(lowest)}
      {affected && (
        <span style={{ marginLeft: 8, fontSize: '0.72rem', fontWeight: 400 }}>
          {t(intl, 'BelowDiscountMarker', 'below discount')}
        </span>
      )}
    </td>
  </tr>
);

/**
 * "Check Applicable Fares" button + axis-grouped result table.
 *
 * @param scope          'band' | 'assignment'
 * @param buildBody      () => object|null — null when the form lacks the inputs
 * @param missingKey     i18n key for the toast when buildBody returns null
 * @param hintKey        i18n key for the tooltip
 * @param discountAmount current fixed discount, for the verdict line
 */
export const ApplicableFaresPanel = ({ scope, buildBody, missingKey, hintKey, discountAmount, intl }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const isArabic = localStorage.getItem('EASY_MOBILITY_LANGUAGE') === 'ar-eg';

  const onClick = async () => {
    const body = buildBody();
    if (!body) {
      toast.error(t(intl, missingKey, 'Please complete the required fields first.'));
      return;
    }
    try {
      setLoading(true);
      setData(await fetchApplicableFares(scope, { ...body, discount_amount: discountAmount || null }));
    } catch (e) {
      toast.error(t(intl, 'FailedToFetchApplicableFares', 'Failed to fetch applicable fares.'));
    } finally {
      setLoading(false);
    }
  };

  const amount = Number(discountAmount);
  // The verdict must mirror what THIS screen's save enforces, or the panel
  // contradicts the outcome. Band saves are bounded by the ceiling, assignment
  // saves by the floor; above the floor but under the ceiling a band is valid
  // to create and only fails later, on the routes priced below it.
  const blocked = !!amount && data && (scope === 'assignment' ? data.would_be_rejected : data.exceeds_max);
  const partial = !!amount && data && scope === 'band' && data.exceeds_min && !data.exceeds_max;
  const flagged = blocked || partial;

  const axisName = (a) => (isArabic ? a.axis_ar_name : a.axis_en_name) || a.axis_code || a.axis_id;
  const routeName = (a) => (isArabic ? a.route_ar_name : a.route_en_name) || '';

  // "17 of 21 axes are cheaper than your discount" is actionable; "rejected on
  // any route priced below 30" makes the admin do the arithmetic themselves.
  const allAxes = data?.axes || [];
  const affectedAxes = amount ? allAxes.filter((a) => a.axis_min_fare !== null && Number(a.axis_min_fare) < amount) : [];
  const isAffected = (lowest) => !!amount && lowest !== null && lowest !== undefined && Number(lowest) < amount;

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <button
          type="button"
          onClick={onClick}
          disabled={loading}
          className="updateBtnStyle"
          style={{ minWidth: 190, display: 'inline-block' }}
        >
          {loading ? 'Loading...' : t(intl, 'CheckApplicableFares', 'Check Applicable Fares')}
        </button>
        <Tooltip title={t(intl, hintKey, 'Shows every chargeable fare — each axis base fare with its zone prices underneath.')}>
          <InfoCircleOutlined style={{ marginLeft: 10, fontSize: 18, color: MUTED, cursor: 'help' }} />
        </Tooltip>
      </div>

      {data && (
        <div style={{ border: `1px solid ${LINE}`, borderRadius: 8, overflow: 'hidden', maxWidth: 760, background: '#fff' }}>

          <div style={{ display: 'flex', background: SURFACE, borderBottom: `1px solid ${LINE}` }}>
            {!!amount && (
              <Stat label={t(intl, 'YourFixedDiscount', 'Your fixed discount')} value={money(amount)} />
            )}
            <Stat
              label={t(intl, 'LowestChargeableFare', 'Lowest chargeable fare')}
              value={money(data.combined_min_fare)}
              note={sourceLabel(data.min_fare_source, data.min_fare_source_name, intl)}
              flagged={flagged}
            />
            <Stat
              label={t(intl, 'HighestChargeableFare', 'Highest chargeable fare')}
              value={money(data.combined_max_fare)}
            />
          </div>

          {!!amount && (
            <div style={{
              padding: '10px 14px',
              borderBottom: `1px solid ${LINE}`,
              borderLeft: `3px solid ${flagged ? ACCENT : 'transparent'}`,
              background: flagged ? ACCENT_SOFT : '#fff',
            }}>
              <div style={{ fontWeight: 600, color: flagged ? ACCENT : INK }}>
                {blocked
                  ? (scope === 'assignment'
                      ? t(intl, 'VerdictBlockedAssignment', 'Cannot save — this discount is larger than the cheapest fare on the selected routes, so those trips would become free.')
                      : t(intl, 'VerdictBlockedBand', 'Cannot save — this discount is larger than every fare in this organisation.'))
                  : partial
                    ? t(intl, 'VerdictPartial', 'Can be saved, but it does not fit every route.')
                    : t(intl, 'VerdictOk', 'Fits every fare listed below.')}
              </div>

              {affectedAxes.length > 0 && (
                <div style={{ fontSize: '0.82rem', color: MUTED, marginTop: 5, lineHeight: 1.6 }}>
                  {t(intl, 'AxesCheaperThanDiscount', 'Axes cheaper than your discount')}:{' '}
                  <strong style={{ color: INK }}>{affectedAxes.length}</strong>
                  <span style={{ color: MUTED }}> / {allAxes.length}</span>
                  {' · '}
                  {t(intl, 'CheapestIs', 'cheapest is')}{' '}
                  <strong style={{ color: INK }}>{money(data.combined_min_fare)}</strong>
                  {' ('}{sourceLabel(data.min_fare_source, data.min_fare_source_name, intl)}{')'}
                  <br />
                  {scope === 'assignment'
                    ? t(intl, 'AffectedHintAssignment', 'Remove those routes from this assignment, or lower the discount.')
                    : t(intl, 'AffectedHintBand', 'Assigning this band to any of them will be rejected. They are highlighted below.')}
                </div>
              )}
            </div>
          )}

          {data.message && (
            <div style={{ padding: '10px 14px', color: MUTED, fontSize: '0.84rem' }}>{data.message}</div>
          )}

          {allAxes.length > 0 && (
            <div style={{ maxHeight: 380, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {[
                      t(intl, 'AxisAndZones', 'Axis / Zone'),
                      t(intl, 'Cash', 'Cash'),
                      t(intl, 'Card', 'Card'),
                      t(intl, 'EffectiveFare', 'Effective fare'),
                    ].map((h, i) => (
                      <th key={i} style={{
                        ...cell, textAlign: isArabic ? 'right' : 'left', fontWeight: 600, color: MUTED,
                        fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em',
                        background: SURFACE, position: 'sticky', top: 0,
                        width: i === 0 ? 'auto' : i === 3 ? 170 : 90,
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allAxes.map((a) => {
                    const axisFlagged = isAffected(a.axis_min_fare);
                    return (
                      <React.Fragment key={a.axis_id}>
                        <tr>
                          <td colSpan={4} style={{
                            ...cell, background: SURFACE, fontWeight: 600,
                            borderLeft: `3px solid ${axisFlagged ? ACCENT : 'transparent'}`,
                            color: axisFlagged ? ACCENT : INK,
                          }}>
                            {axisName(a)}
                            {a.axis_code ? <span style={{ color: MUTED, fontWeight: 400 }}>{` · ${a.axis_code}`}</span> : ''}
                            {routeName(a) ? <span style={{ color: MUTED, fontWeight: 400 }}>{` · ${routeName(a)}`}</span> : ''}
                            {a.route_id && (
                              <button
                                type="button"
                                onClick={() => window.open(`/operations/routes/${a.route_id}`, '_blank')}
                                style={{
                                  marginLeft: 10, padding: '0 6px', fontSize: '0.72rem', borderRadius: 4,
                                  border: `1px solid ${LINE}`, color: MUTED, background: '#fff', cursor: 'pointer',
                                }}
                              >
                                {t(intl, 'Route', 'Route')} ↗
                              </button>
                            )}
                          </td>
                        </tr>

                        {a.base_fares.length === 0 ? (
                          <tr>
                            <td colSpan={4} style={{ ...cell, paddingLeft: 32, color: MUTED }}>
                              {t(intl, 'NoBaseFareOnAxis', 'No base fare configured on this axis')}
                            </td>
                          </tr>
                        ) : (
                          a.base_fares.map((b) => (
                            <PriceRow
                              key={b.base_fare_id}
                              intl={intl}
                              label={t(intl, 'BaseFareLabel', 'Base fare')}
                              sublabel={b.valid_from ? `${t(intl, 'FareValidFrom', 'from')} ${String(b.valid_from).split('T')[0]}` : ''}
                              cash={b.cash_price}
                              card={b.card_price}
                              lowest={b.lowest_price}
                              affected={isAffected(b.lowest_price)}
                            />
                          ))
                        )}

                        {a.zones.length === 0 ? (
                          <tr>
                            <td colSpan={4} style={{ ...cell, paddingLeft: 32, color: MUTED }}>
                              {t(intl, 'NoZonesOnAxis', 'No zones on this axis — base fare applies throughout')}
                            </td>
                          </tr>
                        ) : (
                          a.zones.map((z) => (
                            <PriceRow
                              key={z.zone_id}
                              intl={intl}
                              indented
                              label={z.zone_name || `#${z.zone_id}`}
                              cash={z.cash_price}
                              card={z.card_price}
                              lowest={z.lowest_price}
                              affected={isAffected(z.lowest_price)}
                            />
                          ))
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* A zone whose fareAxisId does not resolve to an axis is still charged
              to passengers, so it must be shown rather than silently dropped. */}
          {data.unattributed && data.unattributed.length > 0 && (
            <div style={{ padding: '10px 14px', borderTop: `1px solid ${LINE}`, borderLeft: `3px solid ${ACCENT}`, background: ACCENT_SOFT }}>
              <div style={{ fontWeight: 600, color: ACCENT, marginBottom: 4, fontSize: '0.84rem' }}>
                {t(intl, 'UnattributedFares', 'Prices not linked to any axis')} ({data.unattributed.length})
              </div>
              {data.unattributed.map((u, i) => (
                <div key={i} style={{ fontSize: '0.82rem', color: MUTED }}>
                  {u.source === 'ZONE' ? (u.source_name || `#${u.source_id}`) : t(intl, 'BaseFareLabel', 'Base fare')}
                  {' · '}{t(intl, 'Cash', 'Cash')} {money(u.cash_price)}
                  {' · '}{t(intl, 'Card', 'Card')} {money(u.card_price)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
